import {Component, computed, effect, inject, signal} from '@angular/core';
import {ActivatedRoute, Router, RouterModule} from '@angular/router';
import {EMPTY} from 'rxjs';
import {catchError, filter, tap} from 'rxjs/operators';
import {rxResource, toSignal} from '@angular/core/rxjs-interop';
import {BreadcrumbModule} from 'primeng/breadcrumb';
import {Accordion, AccordionContent, AccordionHeader, AccordionPanel} from 'primeng/accordion';
import {TranslocoDirective, TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {
  CountryCode,
  DirectusService,
  DirectusTranslationPipe,
  HrTool,
  HrToolServiceModel,
  LanguageCode,
  ModuleFunction,
} from '@frontend/directus';
import {CountryService} from '@frontend/shared';
import {ExtendedHrTool} from '../hr-tool.types';
import {HrToolService} from '../hr-tool.service';
import {Button} from 'primeng/button';
import {Image} from 'primeng/image';
import {Tag} from 'primeng/tag';
import {SafeHtmlComponent} from '../../../shared/components/safe-html-output/safe-html-output.component';
import {SimilarToolsComponent} from '../similar-hr-tool/similar-hr-tool.component';
import {APP_ROUTES} from '../../../app.routes';
import {LoadingComponent} from '../../../shared/components/loading/loading.component';
import {ModuleFunctionLabelComponent} from '../../../shared/components/module-function-label/module-function-label.component';
import {CertificationListComponent} from '../../../shared/components/certification-list/certification-list.component';
import {HrToolAiProfileViewComponent} from '../../../shared/components/hr-tool-ai-profile-view/hr-tool-ai-profile-view.component';
import {HrToolAiCapabilityView} from '@frontend/shared-api';

@Component({
  selector: 'app-hr-tool-detail',
  imports: [
    RouterModule,
    BreadcrumbModule,
    AccordionPanel,
    AccordionHeader,
    AccordionContent,
    Accordion,
    TranslocoDirective,
    TranslocoPipe,
    DirectusTranslationPipe,
    Button,
    Image,
    Tag,
    SafeHtmlComponent,
    SimilarToolsComponent,
    LoadingComponent,
    ModuleFunctionLabelComponent,
    CertificationListComponent,
    HrToolAiProfileViewComponent,
  ],
  templateUrl: './hr-tool-detail.component.html',
})
export class HrToolDetailComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly directusService = inject(DirectusService);
  private readonly transloco = inject(TranslocoService);
  private readonly countryService = inject(CountryService);
  private readonly hrToolsService = inject(HrToolService);

  private readonly routeParams = toSignal(this.route.paramMap);
  private readonly toolId = computed(() => this.routeParams()?.get('id') ?? undefined);

  constructor() {
    this.hrToolsService.ensureArchitecturalPrinciplesLoaded();
    effect(() => {
      if (this.routeParams() && !this.toolId()) {
        this.router.navigate([APP_ROUTES.HR_TOOLS]);
      }
    });
  }

  readonly activeLanguage = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });

  protected readonly toolResource = rxResource({
    params: () => this.toolId(),
    stream: ({params: id}) =>
      this.directusService
        .loadHrToolById(id, {requireActive: true, lang: this.activeLanguage()})
        .pipe(
          tap((tool) => {
            if (!tool) this.router.navigate([APP_ROUTES.HR_TOOLS]);
          }),
          filter((tool): tool is HrTool => tool !== null),
          catchError(() => {
            this.router.navigate([APP_ROUTES.HR_TOOLS]);
            return EMPTY;
          }),
        ),
  });

  readonly hrTool = computed((): ExtendedHrTool | null => {
    const tool = this.toolResource.value();
    if (!tool) return null;
    const translation = tool.translations?.find((t) => t.language === this.activeLanguage());
    return {
      ...tool,
      translatedName: translation?.name ?? tool.name,
      translatedDescription: translation?.description ?? null,
      translatedDetailedDescription: translation?.description_detailed ?? null,
    };
  });

  private readonly failedImageUrls = signal<ReadonlySet<string>>(new Set());

  private readonly referencedImages = computed((): string[] => {
    const tool = this.toolResource.value();
    if (!tool) return [];
    const galleryImageUrls = (tool.images ?? [])
      .map((image) => image.gallery_image)
      .filter((fileId): fileId is string => Boolean(fileId))
      .map((fileId) => this.directusService.getHrCatalogImageUrl(fileId));
    if (tool.coverImage) {
      return [this.directusService.getHrCatalogImageUrl(tool.coverImage), ...galleryImageUrls];
    }
    return galleryImageUrls;
  });

  readonly images = computed((): string[] =>
    this.referencedImages().filter((url) => !this.failedImageUrls().has(url)),
  );

  protected onImageError(url: string): void {
    this.failedImageUrls.update((failed) => new Set(failed).add(url));
  }

  readonly moduleFunctions = computed(
    (): ModuleFunction[] => this.toolResource.value()?.moduleFunctions ?? [],
  );

  readonly toolName = computed(() => this.hrTool()?.translatedName ?? '');

  readonly aiCapabilities = computed(
    (): HrToolAiCapabilityView[] => this.toolResource.value()?.aiCapabilities ?? [],
  );

  readonly availableLanguages = computed(
    (): LanguageCode[] => this.toolResource.value()?.availableLanguages ?? [],
  );

  readonly serviceModels = computed(
    (): HrToolServiceModel[] => this.toolResource.value()?.serviceModels ?? [],
  );

  readonly companySize = computed(() => ({
    min: this.toolResource.value()?.minEmployeeCount ?? null,
    max: this.toolResource.value()?.maxEmployeeCount ?? null,
  }));

  readonly certificationDirectusUuids = computed(
    (): string[] => this.toolResource.value()?.certificationDirectusUuids ?? [],
  );

  private readonly availableCountryCodes = computed(
    () =>
      this.toolResource
        .value()
        ?.availableCountries?.map((c) => c.isoCode)
        .filter((code): code is string => Boolean(code)) ?? [],
  );

  readonly availableCountries = this.countryService.getFilteredAndSortedCountries(
    this.availableCountryCodes,
    this.activeLanguage,
  );

  /** The tool's assigned system type, labeled from the same source as the catalog grid. */
  readonly applicationTypeLabel = computed<string | null>(() => {
    const tool = this.hrTool();
    return tool
      ? this.hrToolsService.applicationTypeLabel(this.hrToolsService.applicationTypeOf(tool))
      : null;
  });

  protected addToComparison(): void {
    const tool = this.toolResource.value();
    if (tool) {
      this.hrToolsService.addToolToComparison(tool);
    }
  }

  readonly breadcrumbItems = computed(() => [
    {
      label: this.transloco.translate('hrTool.breadcrumb.list'),
      routerLink: APP_ROUTES.HR_TOOLS,
    },
    {label: this.hrTool()?.translatedName ?? 'Details'},
  ]);

  getCountryDisplayName(country: CountryCode): string {
    return this.countryService.getCountryDisplayName(country, this.activeLanguage());
  }

  getCountryFlagUrl(countryCode: string): string {
    return this.countryService.getCountryFlagUrl(countryCode);
  }

  getWebsiteUrl(website: string | null | undefined): string {
    if (!website) return '';
    return website.startsWith('http://') || website.startsWith('https://')
      ? website
      : `https://${website}`;
  }
}
